/**
 * Access Denied Component
 *
 * Displayed when a user is authenticated but lacks the required roles
 * to access a specific route.
 */

import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { ShieldX, Home, LogOut } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import type { RoleName } from '@/lib/roles'

interface AccessDeniedProps {
  requiredRoles?: RoleName[]
}

export function AccessDenied({ requiredRoles }: AccessDeniedProps) {
  const { user, logout } = useAuth()

  const handleLogout = async () => {
    await logout()
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-eventus-navy via-eventus-navy-dark to-eventus-purple/20 p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full"
      >
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20 shadow-2xl text-center">
          {/* Icon */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="mx-auto w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-6"
          >
            <ShieldX className="w-8 h-8 text-red-400" />
          </motion.div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>

          {/* Message */}
          <p className="text-white/70 mb-6">
            {user?.name ? (
              <>
                Hi <span className="text-white font-medium">{user.name.split(' ')[0]}</span>, you
                don't have permission to access this page.
              </>
            ) : (
              "You don't have permission to access this page."
            )}
          </p>

          {/* Required roles info */}
          {requiredRoles && requiredRoles.length > 0 && (
            <div className="bg-white/5 rounded-lg p-4 mb-6 text-left">
              <p className="text-white/60 text-sm mb-2">Required role(s):</p>
              <div className="flex flex-wrap gap-2">
                {requiredRoles.map((role) => (
                  <span
                    key={role}
                    className="px-2 py-1 bg-white/10 rounded text-xs text-white/80 font-mono"
                  >
                    {role}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Contact info */}
          <p className="text-white/50 text-sm mb-8">
            If you believe this is an error,{' '}
            <a
              href="mailto:clinicalquality@eventuswh.com?subject=EQuIP%20Access%20Request"
              className="text-eventus-teal hover:text-eventus-teal-light underline transition-colors"
            >
              contact your administrator
            </a>
            .
          </p>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-eventus-teal hover:bg-eventus-teal-light text-eventus-navy font-semibold rounded-full transition-all hover:shadow-lg"
            >
              <Home className="w-4 h-4" />
              Return Home
            </Link>
            <button
              onClick={handleLogout}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white font-medium rounded-full border border-white/20 transition-all"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
